import {
  computeLookAtQuaternion,
  computeOverviewCameraPosition,
} from "@/modes/overview-mode/use-update-overview-camera";
import { CameraView, CameraViewType } from "@/utils/cam-view";
import { DEFAULT_PERSPECTIVE_FOV } from "@faro-lotv/lotv";
import {
  Box3,
  PerspectiveCamera,
  Quaternion,
  Vector3,
  Vector3Tuple,
} from "three";

/** Share of the visible points treated as stray on each horizontal side and below the floor */
const STRAY_SHARE = 0.01;
/** Share of the visible points treated as stray above the roof, smaller as roofs face the camera */
const ROOF_STRAY_SHARE = 0.005;
/** Margin around the visible data, relative to its horizontal extent */
const HORIZONTAL_MARGIN = 0.05;
/** Margin below the floor and above the roof, in meters */
const VERTICAL_MARGIN = 0.5;
/** Height of a static scanner above the floor, in meters */
const SCANNER_HEIGHT = 1.5;
/** Fewer visible points do not describe the data */
const MIN_VISIBLE_POINTS = 50;
/** Visible points closer than this many times their usual spacing are parts of the same object */
const LINK_SPACINGS = 4;
/** Shortest link between visible points, in meters, as close points may be sampled from far away */
const MIN_LINK_DISTANCE = 0.5;
/** Points used to estimate the spacing of the visible points */
const SPACING_SAMPLES = 500;

/**
 * @param sorted Values sorted in ascending order
 * @param share Share of the values below the result
 * @returns the quantile of the values
 */
function quantile(sorted: number[], share: number): number {
  return sorted[Math.min(sorted.length - 1, Math.floor(share * sorted.length))];
}

/**
 * @param points The points
 * @param axis The coordinate to extract
 * @returns the coordinates of the points along the axis, sorted
 */
function sortedAxis(points: Vector3Tuple[], axis: number): number[] {
  return points.map((point) => point[axis]).sort((a, b) => a - b);
}

/**
 * @param sorted Coordinates sorted in ascending order
 * @returns the range of the coordinates without the stray ones, plus the margin
 */
function horizontalRange(sorted: number[]): [number, number] {
  const min = quantile(sorted, STRAY_SHARE);
  const max = quantile(sorted, 1 - STRAY_SHARE);
  const margin = (max - min) * HORIZONTAL_MARGIN;
  return [min - margin, max + margin];
}

/**
 * @param points The points
 * @returns the median horizontal distance from a point to its closest neighbor, on a subset of the points
 */
function medianSpacing(points: Vector3Tuple[]): number {
  const step = Math.max(1, Math.floor(points.length / SPACING_SAMPLES));
  const spacings: number[] = [];
  for (let i = 0; i < points.length; i += step) {
    const [x, , z] = points[i];
    let closest = Infinity;
    for (const [j, [ox, , oz]] of points.entries()) {
      if (j !== i) closest = Math.min(closest, Math.hypot(ox - x, oz - z));
    }
    spacings.push(closest);
  }
  return quantile(
    spacings.sort((a, b) => a - b),
    0.5,
  );
}

/**
 * Keeps the points of the data around the scanners, the stray points are further from it than the data is sparse.
 *
 * @param points The visible points
 * @param scannerPositions World positions of the static scanners
 * @returns the points linked to the scanners through close neighbors, seen from above, or all of them if none is
 */
function keepScannedData(
  points: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
): Vector3Tuple[] {
  const link = Math.max(
    MIN_LINK_DISTANCE,
    LINK_SPACINGS * medianSpacing(points),
  );
  function cellOf(x: number, z: number): string {
    return `${Math.floor(x / link)},${Math.floor(z / link)}`;
  }
  const cells = new Map<string, number[]>();
  for (const [i, [x, , z]] of points.entries()) {
    const key = cellOf(x, z);
    const cell = cells.get(key);
    if (cell) cell.push(i);
    else cells.set(key, [i]);
  }
  function neighbors([x, , z]: Vector3Tuple): number[] {
    return [-1, 0, 1].flatMap((dx) =>
      [-1, 0, 1].flatMap((dz) =>
        (cells.get(cellOf(x + dx * link, z + dz * link)) ?? []).filter(
          (j) => Math.hypot(points[j][0] - x, points[j][2] - z) <= link,
        ),
      ),
    );
  }

  const isKept = new Uint8Array(points.length);
  const queue = [
    ...new Set(scannerPositions.flatMap((position) => neighbors(position))),
  ];
  for (const i of queue) isKept[i] = 1;
  for (let i = queue.pop(); i !== undefined; i = queue.pop()) {
    for (const j of neighbors(points[i])) {
      if (!isKept[j]) {
        isKept[j] = 1;
        queue.push(j);
      }
    }
  }
  const kept = points.filter((_, i) => isKept[i]);
  return kept.length >= MIN_VISIBLE_POINTS ? kept : points;
}

/**
 * Computes the region of the data the user sees, without the stray points (sky, reflections, noise).
 * Seen from above, floors under a roof are hidden, so the floor is also derived from the scanners height.
 *
 * @param visiblePoints The visible points of the point cloud, e.g. from listVisiblePoints
 * @param scannerPositions World positions of the static scanners of the data, if known
 * @returns the region box, or undefined if too few points are visible
 */
export function computeVisibleDataBox(
  visiblePoints: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
): Box3 | undefined {
  if (visiblePoints.length < MIN_VISIBLE_POINTS) return;
  const points = scannerPositions.length
    ? keepScannedData(visiblePoints, scannerPositions)
    : visiblePoints;
  const heights = sortedAxis(points, 1);
  const [minX, maxX] = horizontalRange(sortedAxis(points, 0));
  const [minZ, maxZ] = horizontalRange(sortedAxis(points, 2));
  const floor = scannerPositions.reduce(
    (lowest, position) => Math.min(lowest, position[1] - SCANNER_HEIGHT),
    quantile(heights, STRAY_SHARE),
  );
  const roof = quantile(heights, 1 - ROOF_STRAY_SHARE);
  return new Box3(
    new Vector3(minX, floor - VERTICAL_MARGIN, minZ),
    new Vector3(maxX, roof + VERTICAL_MARGIN, maxZ),
  );
}

/** A screen area in normalized device coordinates */
export type ScreenArea = {
  left: number;
  right: number;
  bottom: number;
  top: number;
};

/** The canvas area free from the right toolbar and the hint banner of the viewer */
export const DEFAULT_HANDLES_AREA: ScreenArea = {
  left: -0.85,
  right: 0.85,
  bottom: -0.85,
  top: 0.65,
};
const FIT_ITERATIONS = 24;

/**
 * @param box The region box
 * @returns the positions of the handles of the box controls, at the center of the faces
 */
function boxHandles(box: Box3): Vector3[] {
  const { x, y, z } = box.getCenter(new Vector3());
  const { min, max } = box;
  return [
    new Vector3(min.x, y, z),
    new Vector3(max.x, y, z),
    new Vector3(x, min.y, z),
    new Vector3(x, max.y, z),
    new Vector3(x, y, min.z),
    new Vector3(x, y, max.z),
  ];
}

/**
 * Moves the camera along its view direction until all the handles of the box controls are in a screen area,
 * so the user can grab them without navigating.
 *
 * @param box The region box
 * @param rotation The camera orientation to keep
 * @param aspect Width / height of the canvas
 * @param area Where the handles must be, usually the canvas area free from toolbars
 * @returns the camera view orbiting around the center of the box
 */
export function computeBoxHandlesFramingView(
  box: Box3,
  rotation: Quaternion,
  aspect: number,
  area: ScreenArea = DEFAULT_HANDLES_AREA,
): CameraView {
  const target = box.getCenter(new Vector3());
  const handles = boxHandles(box);
  const camera = new PerspectiveCamera(DEFAULT_PERSPECTIVE_FOV, aspect);
  camera.quaternion.copy(rotation);
  const direction = camera.getWorldDirection(new Vector3());

  function fits(distance: number): boolean {
    camera.position.copy(target).addScaledVector(direction, -distance);
    camera.updateMatrixWorld();
    return handles.every((handle) => {
      const isInFront =
        handle.clone().applyMatrix4(camera.matrixWorldInverse).z < 0;
      const { x, y } = handle.clone().project(camera);
      return (
        isInFront &&
        x >= area.left &&
        x <= area.right &&
        y >= area.bottom &&
        y <= area.top
      );
    });
  }

  let low = 0;
  let high = Math.max(box.max.distanceTo(box.min), 1);
  for (let i = 0; i < FIT_ITERATIONS && !fits(high); i++) high *= 2;
  for (let i = 0; i < FIT_ITERATIONS; i++) {
    const mid = (low + high) / 2;
    if (fits(mid)) high = mid;
    else low = mid;
  }

  return {
    position: target.clone().addScaledVector(direction, -high),
    rotation: rotation.clone(),
    zoomLevel: DEFAULT_PERSPECTIVE_FOV,
    type: CameraViewType.perspective,
    target,
  };
}

/**
 * Frames a box like the overview mode does when the user selects a scan
 *
 * @param box The world space box to frame, ideally around the scan positions
 * @returns the camera view orbiting around the center of the box
 */
export function computeOverviewFramingView(box: Box3): CameraView {
  const target = box.getCenter(new Vector3());
  const position = computeOverviewCameraPosition(
    target,
    box.max.distanceTo(box.min) / 2,
  );
  return {
    position,
    rotation: computeLookAtQuaternion(position, target),
    zoomLevel: DEFAULT_PERSPECTIVE_FOV,
    type: CameraViewType.perspective,
    target,
  };
}
